import type { FreshnessModule, FreshnessRowDto } from "@gladis/shared";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { t, useT } from "@gladis/ui/i18n";
import { daysBetween, localDay } from "../../lib/household-day.js";

/**
 * ⑦ How old the numbers are — the page's footnote, and its map of what there is to fill in.
 *
 * ==========================================================================================
 * NOT A `StatCard`, AND THE REASON IS ONE MISSING FIELD
 * ==========================================================================================
 *
 * `StatCardRow` is `{ key, label, value, muted? }` — no `href` — and AC-D7 requires every row here to link
 * to its module. Adding a link to that row type would put navigation inside a component whose own
 * documentation says everything in it "must be a number or a name" (research R14). So this is ordinary
 * markup in `apps/app`, like ⑥ beside it, and `@gladis/ui` gains nothing.
 *
 * ==========================================================================================
 * FIVE ROWS, ALWAYS, AND THE LINK SURVIVES "No data yet"
 * ==========================================================================================
 *
 * The server sends five rows in a fixed order with `null` where a module has nothing, and this renders all
 * five. A row that disappeared when empty would take away the one thing an empty dashboard is for: the
 * household needs to see that Income has nothing recorded AND where to go about it (FR-078).
 *
 * MORTGAGES HAVE NO ROW, and the absence is upstream — the payload has five modules, not six. A balance
 * amortised from stored parameters cannot go stale.
 *
 * ==========================================================================================
 * RELATIVE TIME, IN THE HOUSEHOLD'S OWN CALENDAR
 * ==========================================================================================
 *
 * "Updated today", "Updated 3 days ago", "Updated 2 months ago" — because the question this panel answers
 * IS a duration, which is exactly why ⑥ beside it shows an absolute timestamp instead.
 *
 * The day is counted in Europe/Amsterdam rather than in UTC or the browser's zone, and that is the field's
 * own rule (data-model §2.8): it is what decides whether an entry made at 23:40 last night reads as
 * "today" or "yesterday". Two of the five sources are already instants and three are dates widened to
 * midnight UTC, so comparing raw instants would put every date-sourced row two hours into the previous day
 * for a household in summer time.
 */

/** Where each row goes. The one place on this panel that knows a module maps to a route. */
const DESTINATIONS: Record<FreshnessModule, string> = {
  savings: "/savings",
  investments: "/investments",
  properties: "/properties",
  vehicles: "/vehicles",
  expenses: "/expenses",
  income: "/income",
};

/*
 * THIS CONSTANT HOLDS KEYS, NOT SENTENCES — feature 021.
 *
 * A module-scope `t("…")` is evaluated once, when the module is first imported, so the label is
 * frozen in whichever language was loaded at that moment. It renders correctly for whoever opens the
 * application and wrongly for whoever switches, and nothing about it looks wrong.
 */
const LABEL_KEYS: Record<FreshnessModule, string> = {
  savings: "nav.savings",
  investments: "nav.investments",
  properties: "nav.properties",
  vehicles: "veh.nav",
  expenses: "nav.expenses",
  income: "nav.income",
};

/**
 * "Updated today", "Updated 3 days ago", "Updated 2 months ago" — or the empty state.
 *
 * Counted in whole days first, then in months past sixty days, because "Updated 74 days ago" is a number
 * a reader has to convert and "2 months ago" is the answer they were converting it into.
 */
export function freshnessLabel(lastUpdatedAt: string | null, now: Date): string {
  if (lastUpdatedAt === null) return t("dash.freshness.noData");

  const at = new Date(lastUpdatedAt);
  if (Number.isNaN(at.getTime())) return t("dash.freshness.noData");

  const days = daysBetween(localDay(at), localDay(now));

  // A FUTURE DATE IS "today", not "in 3 days". Income entries and savings balances may be dated ahead —
  // the forms permit it — and a freshness panel reporting a negative age would be answering a question
  // nobody asked. What matters is that the module is not stale, and it is not.
  if (days <= 0) return t("dash.freshness.updatedToday");
  if (days === 1) return t("dash.freshness.updatedYesterday");
  if (days < 60) return t("dash.freshness.updatedDaysAgo", { count: days });

  const months = Math.round(days / 30);
  return t("dash.freshness.updatedMonthsAgo", { count: months });
}

export function FreshnessPanel({ rows }: { rows: FreshnessRowDto[] }): ReactNode {
  const t = useT();
  // ONE clock read for the whole panel. Five separate `new Date()` calls could straddle midnight and put
  // two rows in different days, which is a one-in-a-million render that reads as a bug forever.
  const now = new Date();

  return (
    <section
      aria-labelledby="freshness-heading"
      data-testid="freshness-panel"
      className="rounded-control border border-line bg-panel p-5"
    >
      <h2 id="freshness-heading" className="text-xs font-medium tracking-wide text-muted uppercase">
        {t("dash.freshness.heading")}
      </h2>

      <dl className="mt-3 flex flex-col gap-1">
        {rows.map((row) => {
          const age = freshnessLabel(row.lastUpdatedAt, now);

          return (
            <div key={row.module} className="flex items-baseline justify-between gap-3">
              <dt className="text-sm">
                {/*
                THE ACCESSIBLE NAME CARRIES THE AGE, and the visible text does not need to.

                A link reading only "Savings" collides with the nav rail's link of the same name — one
                accessible name, two destinations, on one page. `ExpenseFilters` records what that costs:
                "a screen-reader user hears it twice with nothing to tell them apart, and the ambiguity
                showed up first as a Playwright strict-mode failure." It did here too, and in five places
                at once, breaking every spec that clicks a module in the rail from this page.

                Naming the row for what it says — "Savings, Updated 3 days ago" — is also the better
                reading: a bare "Savings" in a list of five is meaningless out of its row.
              */}
                <Link
                  to={DESTINATIONS[row.module]}
                  aria-label={`${t(LABEL_KEYS[row.module])}, ${age}`}
                  className="text-ink underline hover:text-muted"
                >
                  {t(LABEL_KEYS[row.module])}
                </Link>
              </dt>
              {/*
              INFORMATIONAL ONLY — no colour coding and no staleness alert in v1 (dashboard.md). A red
              row would be this panel deciding what "too old" means for a household that records its
              savings quarterly on purpose.
            */}
              <dd className="text-sm text-muted tabular-nums">{age}</dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}
